import {
  upsertUiComponents,
  type StoredUiComponent,
} from "@/storage/uiComponentsRepository";
import { normalizeUiComponentId } from "@/utils/uiComponentLinks";
import type { UiComponent } from "@/types/ui";

/**
 * Helpers for keeping the embedded component-scheme tree consistent.
 *
 * Child components are stored both as their own records and as embedded
 * snapshots inside every ancestor's `uiScheme.children`. The currently
 * displayed page renders the embedded snapshot of its root component, so any
 * mutation to a child must be propagated up the ancestor chain for the page to
 * re-render with the change.
 */

/**
 * Upserts a component and rewrites every ancestor's embedded copy of it, so a
 * page rendered from an ancestor scheme re-renders with the latest child data.
 */
export async function upsertComponentAndAncestorSchemes(
  scopeKey: string,
  components: readonly StoredUiComponent[],
  updatedComponent: StoredUiComponent,
): Promise<readonly StoredUiComponent[]> {
  const componentsByUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  const changed = new Map<string, StoredUiComponent>();

  propagateComponentUpAncestors(componentsByUuid, changed, updatedComponent);

  return persistChangedComponents(scopeKey, changed);
}

/**
 * Removes several components from the embedded scheme of their parents in one
 * pass, then propagates every touched parent up its own ancestors. Doing this
 * per component would run each removal against the untouched component list, so
 * a parent (or an ancestor) shared by two removed components would keep only the
 * last removal.
 */
export async function removeComponentsFromAncestorSchemes(
  scopeKey: string,
  components: readonly StoredUiComponent[],
  removedComponents: readonly StoredUiComponent[],
): Promise<readonly StoredUiComponent[]> {
  const componentsByUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  const changed = new Map<string, StoredUiComponent>();

  removedComponents.forEach((removedComponent) => {
    const parentUuid = removedComponent.parentUuid;
    if (!parentUuid) return;

    const parent = componentsByUuid.get(parentUuid);
    if (!parent) return;

    propagateComponentUpAncestors(
      componentsByUuid,
      changed,
      updateComponentSchemeChildren(parent, (children) => (
        children.filter(
          (scheme) => !schemeReferencesComponent(scheme, removedComponent),
        )
      )),
    );
  });

  return persistChangedComponents(scopeKey, changed);
}

/**
 * Reparents a component: removes its embedded copy from the previous parent's
 * subtree and (re)inserts it into the current parent's subtree, then upserts the
 * component and every touched ancestor. Both edits run against one shared
 * component map so ancestors common to the old and new parent keep both changes.
 *
 * `repositionedComponents` carries the siblings whose `position` changed with
 * the move. They keep their parent, so they only need their embedded copies
 * refreshed — through the same maps, so one drop produces one persist pass.
 */
export async function moveComponentInAncestorSchemes(
  scopeKey: string,
  components: readonly StoredUiComponent[],
  updatedComponent: StoredUiComponent,
  previousParentUuid: string | null,
  repositionedComponents: readonly StoredUiComponent[] = [],
): Promise<readonly StoredUiComponent[]> {
  const componentsByUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  const changed = new Map<string, StoredUiComponent>();

  if (previousParentUuid && previousParentUuid !== updatedComponent.parentUuid) {
    const previousParent = componentsByUuid.get(previousParentUuid);
    if (previousParent) {
      const detachedParent = updateComponentSchemeChildren(
        previousParent,
        (children) => children.filter(
          (scheme) => !schemeReferencesComponent(scheme, updatedComponent),
        ),
      );
      propagateComponentUpAncestors(componentsByUuid, changed, detachedParent);
    }
  }

  propagateComponentUpAncestors(componentsByUuid, changed, updatedComponent);
  repositionedComponents.forEach((component) => {
    propagateComponentUpAncestors(componentsByUuid, changed, component);
  });

  return persistChangedComponents(scopeKey, changed);
}

/**
 * Walks from `updatedComponent` up its ancestor chain, recording the component
 * and each ancestor whose embedded copy of its child changes into `changed`.
 * Mutations are tracked in `componentsByUuid` so a later walk over a shared
 * ancestor builds on the earlier edit instead of reverting it.
 */
function propagateComponentUpAncestors(
  componentsByUuid: Map<string, StoredUiComponent>,
  changed: Map<string, StoredUiComponent>,
  updatedComponent: StoredUiComponent,
): void {
  let currentComponent: StoredUiComponent | null = updatedComponent;

  while (currentComponent) {
    componentsByUuid.set(currentComponent.uuid, currentComponent);
    changed.set(currentComponent.uuid, currentComponent);

    const parentUuid = currentComponent.parentUuid;
    if (!parentUuid) return;

    const parent = componentsByUuid.get(parentUuid);
    if (!parent) return;

    const componentToPropagate = currentComponent;
    currentComponent = updateComponentSchemeChildren(parent, (children) => {
      const embeddedComponent = createEmbeddedComponentScheme(componentToPropagate);
      let replaced = false;
      const nextChildren = children.map((scheme) => {
        if (!schemeReferencesComponent(scheme, componentToPropagate)) {
          return scheme;
        }

        replaced = true;
        return embeddedComponent;
      });

      return replaced ? nextChildren : [...nextChildren, embeddedComponent];
    });
  }
}

async function persistChangedComponents(
  scopeKey: string,
  changed: ReadonlyMap<string, StoredUiComponent>,
): Promise<readonly StoredUiComponent[]> {
  const changedComponents = [...changed.values()];

  // One write for the whole chain: a write per ancestor would announce the
  // store that many times, and the tree and the canvas are drawn again on
  // every announcement.
  await upsertUiComponents(scopeKey, changedComponents);

  return changedComponents;
}

export function updateComponentSchemeChildren<TComponent extends UiComponent>(
  component: TComponent,
  updateChildren: (children: readonly unknown[]) => readonly unknown[],
): TComponent {
  const scheme = isRecord(component.uiScheme) ? component.uiScheme : {};

  return {
    ...component,
    uiScheme: {
      ...scheme,
      children: sortSchemeChildren(
        updateChildren(readSchemeChildren(scheme)),
      ),
    },
  };
}

export function createEmbeddedComponentScheme(
  component: UiComponent,
): Record<string, unknown> {
  const scheme = isRecord(component.uiScheme) ? component.uiScheme : {};

  return {
    ...scheme,
    parent_uuid: component.parentUuid,
    uuid: component.uuid,
  };
}

export function schemeReferencesComponent(
  value: unknown,
  component: Pick<UiComponent, "componentType" | "name" | "uuid">,
): boolean {
  if (!isRecord(value)) return false;

  const props = readRecordProps(value);
  const uuid = normalizeUiComponentId(
    readString(value.uuid)
    ?? readString(value.component_uuid)
    ?? readString(props.uuid)
    ?? readString(props.component_uuid),
  );
  if (uuid) return uuid === component.uuid;

  const name = readString(props.name)
    ?? readString(value.name)
    ?? readString(props.title)
    ?? readString(value.title);
  const kind = readString(props.kind)
    ?? readString(value.kind)
    ?? readString(value.type);

  return name === component.name && kind === component.componentType;
}

/**
 * Resolves the real component record targeted by a builder selection.
 *
 * Top-level selections (`componentPath === null`) map directly to the component
 * identified by `componentId`. Nested selections walk the root component's
 * embedded scheme to the addressed node and resolve it back to its own record,
 * so editing a child component updates the child rather than the page root.
 * Returns `null` for embedded nodes that are not backed by a component record.
 */
export function resolveSelectedComponent(
  components: readonly StoredUiComponent[],
  selection: { componentId: string; componentPath: string | null },
): StoredUiComponent | null {
  const root = components.find((component) => component.uuid === selection.componentId);
  if (!root) return null;
  if (selection.componentPath === null) return root;

  const indices = selection.componentPath
    .split(".")
    .map((part) => Number(part));
  if (indices.some((index) => !Number.isInteger(index) || index < 0)) {
    return null;
  }

  let node: unknown = root.uiScheme;
  for (const index of indices) {
    const children = isRecord(node) ? readSchemeChildren(node) : [];
    node = children[index];
    if (node === undefined) return null;
  }

  const uuid = readSchemeNodeUuid(node);
  if (!uuid) return null;

  return components.find((component) => component.uuid === uuid) ?? null;
}

/**
 * Builds the component to persist after a property change, preserving the
 * embedded children that the API response omits so the rendered page keeps its
 * child components.
 */
export function mergeUpdatedComponent(
  existing: StoredUiComponent,
  saved: UiComponent,
): StoredUiComponent {
  const savedScheme = isRecord(saved.uiScheme) ? saved.uiScheme : {};
  const savedProps = isRecord(saved.props) ? saved.props : {};

  return {
    ...existing,
    ...saved,
    parentUuid: saved.parentUuid ?? existing.parentUuid,
    uiScheme: {
      ...savedScheme,
      ...savedProps,
      children: readSchemeChildren(existing.uiScheme),
    },
  };
}

export function readSchemeChildren(value: unknown): readonly unknown[] {
  if (!isRecord(value)) return [];
  if (Array.isArray(value.children)) return value.children;

  const props = readRecordProps(value);
  if (Array.isArray(props.children)) return props.children;

  return [];
}

export function collectComponentSubtree(
  components: readonly StoredUiComponent[],
  rootComponent: StoredUiComponent,
): readonly StoredUiComponent[] {
  const componentsByUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  const childrenByParentUuid = new Map<string, StoredUiComponent[]>();

  components.forEach((component) => {
    if (!component.parentUuid) return;

    const children = childrenByParentUuid.get(component.parentUuid) ?? [];
    children.push(component);
    childrenByParentUuid.set(component.parentUuid, children);
  });

  const root = componentsByUuid.get(rootComponent.uuid) ?? rootComponent;
  const visited = new Set<string>();
  const visit = (component: StoredUiComponent): StoredUiComponent[] => {
    if (visited.has(component.uuid)) return [];
    visited.add(component.uuid);

    return [
      component,
      ...(childrenByParentUuid.get(component.uuid) ?? []).flatMap(visit),
    ];
  };

  return visit(root);
}

export function collectComponentDeleteOrder(
  components: readonly StoredUiComponent[],
  rootComponent: StoredUiComponent,
): readonly StoredUiComponent[] {
  return [...collectComponentSubtree(components, rootComponent)].reverse();
}

/**
 * Locates a component within its top-level root so an isolated ("focused")
 * subtree can be rendered while its selection ids stay anchored to that root
 * (matching the inspector tree). Returns the top-level root's id and the index
 * path from the root's scheme down to the focused component. A focused
 * top-level component (or one whose path cannot be resolved) is rendered as its
 * own root with an empty base path.
 */
export function resolveComponentFocusLocation(
  components: readonly StoredUiComponent[],
  focusComponentId: string,
): { rootComponentId: string; basePath: readonly number[] } | null {
  const focused = components.find(
    (component) => component.uuid === focusComponentId,
  );
  if (!focused) return null;

  const byUuid = new Map(
    components.map((component) => [component.uuid, component]),
  );
  let root = focused;
  const visited = new Set<string>();
  while (
    root.parentUuid
    && byUuid.has(root.parentUuid)
    && !visited.has(root.uuid)
  ) {
    visited.add(root.uuid);
    root = byUuid.get(root.parentUuid) as StoredUiComponent;
  }

  if (root.uuid === focused.uuid) {
    return { rootComponentId: focused.uuid, basePath: [] };
  }

  const path = findSchemePathToUuid(root.uiScheme, focused.uuid);
  if (!path || path.length === 0) {
    return { rootComponentId: focused.uuid, basePath: [] };
  }
  return { rootComponentId: root.uuid, basePath: path };
}

function findSchemePathToUuid(
  scheme: unknown,
  targetUuid: string,
): number[] | null {
  const children = readSchemeChildren(scheme);
  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    if (readSchemeNodeUuid(child) === targetUuid) return [index];

    const nested = findSchemePathToUuid(child, targetUuid);
    if (nested) return [index, ...nested];
  }
  return null;
}

function readSchemeNodeUuid(value: unknown): string | null {
  if (!isRecord(value)) return null;

  const props = readRecordProps(value);

  return normalizeUiComponentId(
    readString(value.uuid)
    ?? readString(value.component_uuid)
    ?? readString(props.uuid)
    ?? readString(props.component_uuid),
  );
}

function sortSchemeChildren(children: readonly unknown[]): unknown[] {
  return children
    .map((child, index) => ({
      child,
      index,
      position: readSchemeNodePosition(child),
    }))
    .sort((a, b) => (
      a.position - b.position
      || a.index - b.index
    ))
    .map((entry) => entry.child);
}

export function readSchemeNodePosition(value: unknown): number {
  if (!isRecord(value)) return 0;

  const props = readRecordProps(value);
  return readNumber(value.position ?? props.position);
}

function readNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const parsed = typeof value === "string" ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function readRecordProps(value: Record<string, unknown>): Record<string, unknown> {
  return isRecord(value.props) ? value.props : {};
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
